/**
 * Authentication Module
 * 
 * Handles phone + Telegram 2FA login flow.
 */

import { api } from './api';

// DOM Elements
let phoneForm: HTMLFormElement;
let codeForm: HTMLFormElement;
let phoneStep: HTMLElement;
let codeStep: HTMLElement;
let phoneInput: HTMLInputElement;
let codeInput: HTMLInputElement;
let phoneError: HTMLElement;
let codeError: HTMLElement;
let backButton: HTMLElement;

// State
let currentChallengeId: string | null = null;

/**
 * Initialize authentication UI and handlers.
 */
export function initAuth(): void {
    // Get DOM elements
    phoneForm = document.getElementById('phone-form') as HTMLFormElement;
    codeForm = document.getElementById('code-form') as HTMLFormElement;
    phoneStep = document.getElementById('phone-step') as HTMLElement;
    codeStep = document.getElementById('code-step') as HTMLElement;
    phoneInput = document.getElementById('phone-input') as HTMLInputElement;
    codeInput = document.getElementById('code-input') as HTMLInputElement;
    phoneError = document.getElementById('phone-error') as HTMLElement;
    codeError = document.getElementById('code-error') as HTMLElement;
    backButton = document.getElementById('back-to-phone') as HTMLElement;

    // Bind handlers
    phoneForm.addEventListener('submit', handlePhoneSubmit);
    codeForm.addEventListener('submit', handleCodeSubmit);
    backButton.addEventListener('click', showPhoneStep);

    // Auto-advance on 6 digits
    codeInput.addEventListener('input', () => {
        const value = codeInput.value.replace(/\D/g, '').slice(0, 6);
        codeInput.value = value;
        if (value.length === 6) {
            codeForm.requestSubmit();
        }
    });
}

/**
 * Check if user is already authenticated.
 */
export function checkExistingAuth(): boolean {
    return api.isAuthenticated();
}

/**
 * Handle phone number submission.
 */
async function handlePhoneSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const phone = normalizePhone(phoneInput.value);
    if (!phone) {
        showError(phoneError, 'Please enter a valid phone number');
        return;
    }

    setLoading(phoneForm, true);
    hideError(phoneError);

    try {
        const result = await api.requestChallenge(phone);
        currentChallengeId = result.challenge_id;
        showCodeStep();
    } catch (err) {
        showError(phoneError, err instanceof Error ? err.message : 'Request failed');
    } finally {
        setLoading(phoneForm, false);
    }
}

/**
 * Handle verification code submission.
 */
async function handleCodeSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const code = codeInput.value.trim();
    if (!code || code.length !== 6 || !currentChallengeId) {
        showError(codeError, 'Please enter the 6-digit code');
        return;
    }

    setLoading(codeForm, true);
    hideError(codeError);

    try {
        await api.verifyChallenge(currentChallengeId, code);
        // Success - dispatch event for main.ts to handle
        window.dispatchEvent(new CustomEvent('auth:success'));
    } catch (err) {
        showError(codeError, err instanceof Error ? err.message : 'Verification failed');
    } finally {
        setLoading(codeForm, false);
    }
}

/**
 * Handle logout.
 */
export async function logout(): Promise<void> {
    try {
        await api.logout();
    } catch {
        // Ignore errors, just clear local state
    }
    window.dispatchEvent(new CustomEvent('auth:logout'));
}

// ============================================================================
// UI Helpers
// ============================================================================

function showCodeStep(): void {
    phoneStep.classList.add('hidden');
    codeStep.classList.remove('hidden');
    codeInput.value = '';
    codeInput.focus();
}

function showPhoneStep(): void {
    codeStep.classList.add('hidden');
    phoneStep.classList.remove('hidden');
    currentChallengeId = null;
    phoneInput.focus();
}

function showError(el: HTMLElement, message: string): void {
    el.textContent = message;
    el.classList.remove('hidden');
}

function hideError(el: HTMLElement): void {
    el.classList.add('hidden');
}

function setLoading(form: HTMLFormElement, loading: boolean): void {
    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const btnText = btn.querySelector('.btn-text') as HTMLElement;
    const btnLoading = btn.querySelector('.btn-loading') as HTMLElement;

    btn.disabled = loading;
    btnText.classList.toggle('hidden', loading);
    btnLoading.classList.toggle('hidden', !loading);
}

function normalizePhone(phone: string): string {
    // Remove all non-digit characters except leading +
    let normalized = phone.trim();
    const hasPlus = normalized.startsWith('+');
    normalized = normalized.replace(/\D/g, '');
    if (hasPlus && !normalized.startsWith('+')) {
        normalized = '+' + normalized;
    }
    return normalized.length >= 10 ? normalized : '';
}
